/* eslint-disable @typescript-eslint/naming-convention */
import { assign } from 'xstate';
import { PatientFormsContext, PatientFormsEvent } from './patient-forms.types';
import { capitalizeWords } from '../utils/capitalize-words';

type EventOf<T extends PatientFormsEvent['type']> = Extract<PatientFormsEvent, { type: T }>;

export const actions = {
	assignPendingFromEvent: assign<PatientFormsContext, EventOf<'SEARCH.PATIENT_SELECTED'>>({
		pendingIteroPatient: (_, event) => (event.patient.system === 'iTero' ? event.patient : null),
		pendingInvisalignPatient: (_, event) => (event.patient.system === 'Invisalign' ? event.patient : null),
		originalIteroPatient: (_, event) => (event.patient.system === 'iTero' ? event.patient : null)
	}),

	commitLinkedFromEvent: assign<PatientFormsContext, EventOf<'SEARCH.PATIENT_SELECTED'>>({
		iTeroPatient: (_, event) => event.patient,
		invisalignPatient: (_, event) => event.patient,
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null,
		cardMatchingPatients: () => [],
		originalIteroPatient: (_, event) => event.patient
	}),

	commitPendingToItero: assign<PatientFormsContext, PatientFormsEvent>({
		iTeroPatient: context => context.pendingIteroPatient,
		pendingIteroPatient: () => null,
		cardMatchingPatients: () => [],
		matchingPatients: () => []
	}),

	commitPendingToInvisalign: assign<PatientFormsContext, PatientFormsEvent>({
		invisalignPatient: context => context.pendingInvisalignPatient,
		pendingInvisalignPatient: () => null,
		matchingPatients: () => []
	}),

	commitBothPending: assign<PatientFormsContext, PatientFormsEvent>({
		iTeroPatient: context => context.pendingIteroPatient,
		invisalignPatient: context => context.pendingInvisalignPatient,
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null,
		cardMatchingPatients: () => [],
		matchingPatients: () => []
	}),

	commitLinkedPatients: assign<PatientFormsContext, EventOf<'LINKING.LINKED'>>({
		iTeroPatient: (_, event) => event.iTeroPatient,
		invisalignPatient: (_, event) => event.invisalignPatient,
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null,
		cardMatchingPatients: () => [],
		matchingPatients: () => [],
		originalIteroPatient: (_, event) => event.iTeroPatient
	}),

	copyCommittedInvisalignToItero: assign<PatientFormsContext, PatientFormsEvent>({
		pendingIteroPatient: context => {
			if (!context.invisalignPatient) {
				return null;
			}

			return { ...context.invisalignPatient, id: null, system: 'iTero' as const };
		}
	}),

	copyPendingInvisalignToItero: assign<PatientFormsContext, PatientFormsEvent>({
		pendingIteroPatient: context => {
			if (!context.pendingInvisalignPatient) {
				return null;
			}

			return { ...context.pendingInvisalignPatient, id: null, system: 'iTero' as const };
		}
	}),

	copyCommittedIteroToInvisalign: assign<PatientFormsContext, PatientFormsEvent>({
		invisalignPatient: context => {
			const source = context.pendingIteroPatient || context.iTeroPatient;

			if (!source) {
				return null;
			}

			return { ...source, id: null, system: 'Invisalign' as const };
		}
	}),

	clearPending: assign<PatientFormsContext, PatientFormsEvent>({
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null,
		originalIteroPatient: () => null
	}),

	clearPendingKeepOriginal: assign<PatientFormsContext, PatientFormsEvent>({
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null
	}),

	clearAll: assign<PatientFormsContext, PatientFormsEvent>({
		iTeroPatient: () => null,
		invisalignPatient: () => null,
		cardMatchingPatients: () => [],
		matchingPatients: () => [],
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null,
		originalIteroPatient: () => null
	}),

	updateIteroPatient: assign<PatientFormsContext, EventOf<'CARD.FORM_UPDATED'>>({
		iTeroPatient: (_, event) => ({
			...event.patient,
			firstName: capitalizeWords(event.patient.firstName),
			lastName: capitalizeWords(event.patient.lastName)
		})
	}),

	updatePendingIteroFromEditor: assign<PatientFormsContext, EventOf<'EDITOR.SAVED'>>({
		pendingIteroPatient: (_, event) => event.patient
	}),

	commitIteroOnError: assign<PatientFormsContext, PatientFormsEvent>({
		iTeroPatient: context => context.pendingIteroPatient,
		invisalignPatient: () => null,
		pendingIteroPatient: () => null,
		pendingInvisalignPatient: () => null
	}),

	clearInvisalignData: assign<PatientFormsContext, PatientFormsEvent>({
		invisalignPatient: () => null,
		invisalignToggle: () => false,
		pendingInvisalignPatient: () => null
	}),

	setInvisalignToggleOn: assign<PatientFormsContext, PatientFormsEvent>({
		invisalignToggle: () => true
	}),

	copyIteroToInvisalign: assign<PatientFormsContext, PatientFormsEvent>({
		invisalignPatient: context => {
			if (!context.iTeroPatient) {
				return null;
			}

			return { ...context.iTeroPatient, id: null, system: 'Invisalign' as const };
		}
	}),

	clearInvisalignContext: assign<PatientFormsContext, PatientFormsEvent>({
		invisalignPatient: () => null
	}),

	assignCardMatchingPatients: assign<PatientFormsContext, EventOf<'CARD.MATCHES_FOUND'>>({
		cardMatchingPatients: (_, event) => {
			const iteroMatches = event.patients.filter(p => p.system === 'iTero' || p.system === 'both');

			return iteroMatches.length > 0 ? iteroMatches : event.patients;
		}
	}),

	clearCardMatchingPatients: assign<PatientFormsContext, PatientFormsEvent>({
		cardMatchingPatients: () => []
	}),

	assignMatchResults: assign<PatientFormsContext, EventOf<'PATIENT_MATCH.MATCHES_FOUND'>>({
		matchingPatients: (_, event) => event.matches,
		canCreateNew: (_, event) => event.canCreateNew
	}),

	assignSearchResults: assign<PatientFormsContext, EventOf<'SEARCH.RESULTS'>>({
		searchResults: (_, event) => event.patients
	}),

	clearSearchResults: assign<PatientFormsContext, PatientFormsEvent>({
		searchResults: () => []
	}),

	assignDuplicateError: assign<PatientFormsContext, EventOf<'EDITOR.DUPLICATE_FOUND'>>({
		duplicateError: (_, event) => event.duplicate
	}),

	clearDuplicateError: assign<PatientFormsContext, PatientFormsEvent>({
		duplicateError: () => null
	}),

	loadExistingPatient: assign<PatientFormsContext, EventOf<'EXISTING.PATIENT_LOADED'>>({
		iTeroPatient: (_, event) => event.patient,
		invisalignPatient: (_, event) => (event.patient.system === 'both' ? event.patient : null),
		originalIteroPatient: (_, event) => event.patient,
		invisalignToggle: () => false
	}),

	copyIteroToPending: assign<PatientFormsContext, PatientFormsEvent>({
		pendingIteroPatient: context => context.iTeroPatient
	}),

	setDefaultInvisalignToggle: assign<PatientFormsContext, EventOf<'CARD.SET_DEFAULT_TOGGLE'>>({
		defaultInvisalignToggle: (_, event) => event.on
	})
};
